/**
 * Может ли пуш дойти до ЭТОГО браузера. Веб-двойник `detectNativePushState`.
 *
 * Модуль намеренно чистый: ни React, ни обращений к `window` при импорте, потому что он
 * попадает в серверный бандл. Всё, что знает о браузере, спрятано в `readNotificationPermission`
 * и вызывается только из эффекта или ленивого инициализатора состояния.
 */
export type WebPushState =
  /** Ещё не знаем: сервер или первый рендер до гидратации. Молчим. */
  | 'unknown'
  /** Notification API в этом браузере нет вовсе. */
  | 'unsupported'
  /** Разрешение ни разу не спрашивали. Единственное состояние, в котором мы заговариваем. */
  | 'prompt'
  /** Заблокировано настройкой сайта. Переспросить диалогом нельзя. */
  | 'denied'
  | 'ok';

/**
 * Разрешение браузера или `null`, если спрашивать не у кого.
 *
 * Две проверки, и обе обязательны: на сервере `window` нет, а в Safari на iOS нет самого
 * `Notification`, пока сайт не добавили на домашний экран.
 */
export const readNotificationPermission = (): NotificationPermission | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  if (!('Notification' in window)) {
    return null;
  }

  return Notification.permission;
};

/**
 * Состояние по разрешению.
 *
 * `granted` это сразу `ok`, без веб-аналога `no-token-on-server`, и это осознанно.
 * Недостающий токен в вебе уже чинит `useWebPushTokenSync` (смонтирован в `__root.tsx` и
 * работает на каждой загрузке), причём честнее: он сверяет токен ИМЕННО этого браузера со
 * списком сервера, а не пустоту всего списка. Второй хозяин у этой починки означал бы две
 * гонящиеся мутации `addPushToken` и два сброса кэша `me`.
 *
 * `unsupported` не показывает ничего: шторка, единственная кнопка которой не может сработать,
 * хуже молчания. Про неподдерживаемый браузер человек узнаёт в настройках профиля, где у
 * баннера свой детектор.
 *
 * Ловушка на будущее: тот же баннер в настройках называет состояние `default` словом
 * `unregistered`, а `pushAttention` возвращает `null` на любое слово, которого не знает.
 * Поэтому здесь именно `prompt`, слово из `PushAttentionReason`, и сузить состояние до него
 * нужно ДО обращения к общему движку отката, иначе шторка молча не покажется никогда.
 */
export const detectWebPushState = ({
  permission,
  inBrowser,
}: {
  permission: NotificationPermission | null;
  inBrowser: boolean;
}): WebPushState => {
  if (!inBrowser) {
    return 'unknown';
  }
  if (permission === null) {
    return 'unsupported';
  }
  if (permission === 'denied') {
    return 'denied';
  }
  if (permission === 'default') {
    return 'prompt';
  }

  return 'ok';
};
